import { useEffect, useState } from "react";
import { Container, Row, Col, Button, Nav } from "react-bootstrap";
import { ApiKey, ApiKeysService, CreateApiKeyResponse } from "../../../services/apiKeysService";
import ApiKeyList from "./ApiKeyList";
import CreateApiKeyForm from "./CreateApiKeyForm";
import ApiKeyModal from "./ApiKeyModal";

type ViewMode = "list" | "create";

export default function ApiKeyManagement() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newApiKey, setNewApiKey] = useState<{ key: string; name: string } | null>(null);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const keys = await ApiKeysService.getApiKeys();
      setApiKeys(keys);
    } catch (err) {
      console.error("Failed to fetch API keys:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load API keys. Please refresh the page and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleApiKeyCreated = (response: CreateApiKeyResponse, keyName: string) => {
    setNewApiKey({ key: response.api_key, name: keyName });
    setViewMode("list");
    fetchApiKeys(); // Refresh the list
  };

  const handleApiKeyDeleted = (keyId: string) => {
    setApiKeys((prevKeys) => prevKeys.filter((key) => key.key_id !== keyId));
  };

  const handleModalClose = () => {
    setNewApiKey(null);
  };

  const handleRetry = () => {
    fetchApiKeys();
  };

  return (
    <Container fluid className="p-0">

      {/* Error State */}
      {error && (
        <Container className="mb-4 p-3" style={{
          backgroundColor: '#83030320',
          border: '1px solid #83030350',
          borderRadius: '8px'
        }}>
          <Row className="align-items-center">
            <Col xs="auto" className="pe-3">
              <div style={{ color: '#ff4444', fontSize: '24px' }}>⚠️</div>
            </Col>
            <Col>
              <div className="text-danger mb-2" style={{ fontWeight: '600' }}>
                Error Loading API Keys
              </div>
              <div className="text-color mb-2" style={{ fontSize: '14px' }}>
                {error}
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRetry}
                className="wind-btn-outline"
              >
                🔄 Try Again
              </Button>
            </Col>
          </Row>
        </Container>
      )}

      {/* Content based on view mode */}
      {!error && (
        <>
          {viewMode === "list" && (
            <ApiKeyList
              apiKeys={apiKeys}
              onApiKeyDeleted={handleApiKeyDeleted}
              onCreateKey={() => setViewMode("create")}
              isLoading={isLoading}
            />
          )}

          {viewMode === "create" && (
            <CreateApiKeyForm
              onApiKeyCreated={handleApiKeyCreated}
              isLoading={isLoading}
            />
          )}
        </>
      )}

      {/* Quick Actions (visible when not creating) */}
      {viewMode === "list" && !isLoading && !error && (
        <Container fluid className="mt-4">
          <Row className="justify-content-between align-items-center">
            <Col>
              <div className="text-color" style={{ fontSize: '14px' }}>
                {apiKeys.length === 0
                  ? "Get started by creating your first API key"
                  : `You have ${apiKeys.length} API key${apiKeys.length === 1 ? '' : 's'}`
                }
              </div>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setViewMode("create")}
                className="wind-btn"
              >
                ➕ Create New Key
              </Button>
            </Col>
          </Row>
        </Container>
      )}

      {/* API Key Modal */}
      <ApiKeyModal
        show={newApiKey !== null}
        onHide={handleModalClose}
        apiKey={newApiKey?.key || ""}
        keyName={newApiKey?.name || ""}
      />
    </Container>
  );
}